import React, { useState, useMemo } from 'react';
import {
  Container,
  Stack,
  Grid,
  Paper,
  Text,
  Group,
  Badge,
  RingProgress,
  Center,
  Alert,
  Button,
  Box,
  ThemeIcon,
  Card as MantineCard,
  SimpleGrid,
  Popover,
  Accordion,
  Modal,
  ScrollArea,
  Skeleton
} from '@mantine/core';
import {
  IconBook,
  IconCalendar,
  IconActivity,
  IconAlertCircle,
  IconChevronRight,
  IconFilter,
  IconCalendarEvent
} from '@tabler/icons-react';
import DateRangeFilter from 'components/DateRangeFilter';
import PrimaryButton from 'components/PrimaryButton';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import PageTitle from 'components/PageTitle';
import PAGES from 'constant/pages';
import { useAppName } from 'contexts/AppProvider';
import { courseService } from 'services/lcms/course.service';

interface Course {
  courseId: string;
  courseName: string;
  category: string | null;
}

interface StatusCount {
  status: string;
  count: number;
  courses: Course[];
}

interface ActivityTypeCounts {
  scrom: number;
  resource: number;
  other: number;
}

interface YearlyCalendarCourse {
  msLCMSPlanningID: string;
  msLCMSCourseID: string | null;
  planningName: string;
  planningStart: string;
  planningEnd: string;
  courses: Course[];
}

interface CourseByArea {
  area: string;
  total: number;
  courses: Course[];
}

interface OverviewData {
  totalCourses: number;
  statusCounts: StatusCount[];
  yearlyCalendarCourses: YearlyCalendarCourse[];
  yearlyCalendarDistinctCount: number;
  activityTypeCounts: ActivityTypeCounts;
  totalCourseByArea: CourseByArea[];
  totalCoursesList: Course[];
}

const getStatusColor = (status: string) => {
  const colors: Record<string, string> = {
    Active: 'green',
    Draft: 'gray',
    Undefined: 'orange',
    Upcoming: 'blue',
    Completed: 'teal'
  };
  return colors[status] || 'gray';
};

type DateRange = [Date | null, Date | null];

// Default: 59 days ago to 60 days from today
const getDefaultDateRange = (): DateRange => [
  dayjs().subtract(59, 'days').toDate(),
  dayjs().add(60, 'days').toDate()
];

const isSameDateRange = (a: DateRange, b: DateRange) =>
  a[0]?.getTime() === b[0]?.getTime() && a[1]?.getTime() === b[1]?.getTime();

const LCMSOverviewPage = () => {
  const { appName } = useAppName();
  const navigate = useNavigate();

  const [dateRange, setDateRange] = useState<DateRange>(getDefaultDateRange);
  // Edited range, applied to `dateRange` only on Apply (same flow as the LIA Overview filter)
  const [draftDateRange, setDraftDateRange] = useState<DateRange>(dateRange);

  // Modal state for viewing courses
  const [modalOpened, setModalOpened] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalCourses, setModalCourses] = useState<Course[]>([]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['lcmsOverview', dateRange],
    queryFn: () =>
      courseService.getOverview({
        startDate: dateRange[0] ? dayjs(dateRange[0]).format('YYYY-MM-DD') : undefined,
        endDate: dateRange[1] ? dayjs(dateRange[1]).format('YYYY-MM-DD') : undefined
      }),
    enabled: !!(dateRange[0] && dateRange[1])
  });

  const overviewData = (data?.data as unknown as OverviewData) || null;

  // Calculate total status percentage for ring progress
  const statusData = useMemo(() => {
    if (!overviewData?.statusCounts) return [];
    const total = overviewData.totalCourses || 1;
    return overviewData.statusCounts.map((item) => ({
      status: item.status || 'Undefined',
      count: item.count,
      courses: item.courses,
      percentage: (item.count / total) * 100,
      color: getStatusColor(item.status || 'Undefined')
    }));
  }, [overviewData]);

  // Handler to open modal with courses
  const handleViewCourses = (title: string, courses: Course[]) => {
    setModalTitle(title);
    setModalCourses(courses);
    setModalOpened(true);
  };

  // Group calendar courses by month
  const groupedCalendarCourses = useMemo(() => {
    if (!overviewData?.yearlyCalendarCourses) return [];

    const grouped = overviewData.yearlyCalendarCourses.reduce(
      (acc, course) => {
        const monthKey = dayjs(course.planningStart).format('MMMM YYYY');
        if (!acc[monthKey]) {
          acc[monthKey] = [];
        }
        acc[monthKey].push(course);
        return acc;
      },
      {} as Record<string, YearlyCalendarCourse[]>
    );

    return Object.entries(grouped).map(([month, courses]) => ({
      month,
      courses
    }));
  }, [overviewData]);

  // Prepare course by area data
  const courseByAreaData = useMemo(() => {
    if (!overviewData?.totalCourseByArea) return [];
    return overviewData.totalCourseByArea.sort((a, b) => b.total - a.total);
  }, [overviewData]);

  const totalCoursesByArea = useMemo(() => {
    if (!courseByAreaData.length) return 0;
    return courseByAreaData.reduce((sum, item) => sum + item.total, 0);
  }, [courseByAreaData]);

  // Calculate total unique courses in all calendar plans
  const totalCoursesInPlans = useMemo(() => {
    if (!overviewData?.yearlyCalendarCourses) return 0;
    const uniqueCourseIds = new Set<string>();
    overviewData.yearlyCalendarCourses.forEach((plan) => {
      plan.courses?.forEach((course) => {
        if (course.courseId) {
          uniqueCourseIds.add(course.courseId);
        }
      });
    });
    return uniqueCourseIds.size;
  }, [overviewData]);

  if (isLoading) {
    return (
      <Container fluid>
        <Stack gap='lg'>
          {/* Header Skeleton */}
          <Group justify='space-between' align='flex-start'>
            <Box>
              <Skeleton height={32} width={200} mb={8} />
              <Skeleton height={16} width={300} />
            </Box>
            <Skeleton height={36} width={250} />
          </Group>

          {/* Key Metrics Cards Skeleton */}
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing='lg'>
            {[1, 2, 3, 4].map((i) => (
              <Paper key={i} shadow='sm' p='lg' radius='md' withBorder>
                <Group justify='space-between' mb='xs'>
                  <Skeleton height={14} width={80} />
                  <Skeleton height={36} width={36} radius='md' />
                </Group>
                <Skeleton height={28} width={60} mb='xs' />
                <Skeleton height={12} width={120} />
              </Paper>
            ))}
          </SimpleGrid>

          <Grid gutter='lg'>
            {/* Course Status Distribution Skeleton */}
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Paper shadow='sm' p='lg' radius='md' withBorder h='100%'>
                <Group justify='space-between' mb='md'>
                  <div>
                    <Skeleton height={20} width={200} mb={8} />
                    <Skeleton height={12} width={150} />
                  </div>
                </Group>

                <Stack gap='lg'>
                  <Center>
                    <Skeleton height={200} width={200} circle />
                  </Center>

                  <Stack gap='xs'>
                    {[1, 2, 3, 4].map((i) => (
                      <Paper key={i} p='xs' withBorder>
                        <Group justify='space-between'>
                          <Group gap='xs'>
                            <Skeleton height={12} width={12} circle />
                            <Skeleton height={14} width={80} />
                          </Group>
                          <Group gap='xs'>
                            <Skeleton height={14} width={30} />
                            <Skeleton height={12} width={50} />
                          </Group>
                        </Group>
                      </Paper>
                    ))}
                  </Stack>

                  <Skeleton height={36} />
                </Stack>
              </Paper>
            </Grid.Col>

            {/* Course Planning Skeleton */}
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Paper shadow='sm' p='lg' radius='md' withBorder h='100%'>
                <Group justify='space-between' mb='md'>
                  <div>
                    <Skeleton height={20} width={150} mb={8} />
                    <Skeleton height={12} width={120} />
                  </div>
                  <Group gap='xs'>
                    <Skeleton height={22} width={70} radius='xl' />
                    <Skeleton height={22} width={90} radius='xl' />
                  </Group>
                </Group>

                <Stack gap='xs'>
                  {[1, 2, 3].map((i) => (
                    <Box key={i}>
                      <Group justify='space-between' mb='xs'>
                        <Skeleton height={16} width={120} />
                        <Skeleton height={20} width={60} radius='xl' />
                      </Group>
                      <Stack gap='xs' pl='md'>
                        {[1, 2].map((j) => (
                          <Paper key={j} p='sm' withBorder>
                            <Skeleton height={14} width='100%' mb={8} />
                            <Skeleton height={12} width={150} mb={8} />
                            <Skeleton height={18} width={80} radius='xl' />
                          </Paper>
                        ))}
                      </Stack>
                    </Box>
                  ))}
                </Stack>

                <Skeleton height={36} mt='md' />
              </Paper>
            </Grid.Col>

            {/* Course by Area Skeleton */}
            <Grid.Col span={12}>
              <Paper shadow='sm' p='lg' radius='md' withBorder>
                <Group justify='space-between' mb='md'>
                  <div>
                    <Skeleton height={20} width={180} mb={8} />
                    <Skeleton height={12} width={250} />
                  </div>
                  <Skeleton height={22} width={80} radius='xl' />
                </Group>

                <Stack gap='md'>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Paper key={i} p='sm' withBorder>
                      <Group justify='space-between' mb={6}>
                        <Skeleton height={14} width={120} />
                        <Group gap={8}>
                          <Skeleton height={14} width={30} />
                          <Skeleton height={12} width={50} />
                        </Group>
                      </Group>
                      <Skeleton height={8} width='100%' radius={4} />
                    </Paper>
                  ))}
                </Stack>
              </Paper>
            </Grid.Col>
          </Grid>
        </Stack>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid>
        <Stack gap='md'>
          <PageTitle title={PAGES[appName]?.lcmsOverview?.title || 'Overview'} />
          <Alert icon={<IconAlertCircle size={16} />} title='Error' color='red'>
            Failed to load overview data. Please try again later.
          </Alert>
        </Stack>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Stack gap='lg'>
        {/* Header with Date Filter */}
        <Group justify='space-between' align='flex-start'>
          <PageTitle title={PAGES[appName]?.lcmsOverview?.title || 'Overview'} />

          <Popover position='bottom-end' shadow='md'>
            <Popover.Target>
              <Button leftSection={<IconFilter size={16} />} variant='light' size='sm'>
                {dateRange[0] && dateRange[1]
                  ? `${dayjs(dateRange[0]).format('MMM DD, YYYY')} - ${dayjs(dateRange[1]).format('MMM DD, YYYY')}`
                  : 'Select Date Range'}
              </Button>
            </Popover.Target>
            <Popover.Dropdown>
              <Stack gap='sm'>
                <Text size='sm' fw={500}>
                  Filter by Date Range
                </Text>
                <DateRangeFilter value={draftDateRange} onChange={setDraftDateRange} />
                <Group gap='xs' justify='flex-end'>
                  <Button
                    size='xs'
                    variant='subtle'
                    onClick={() => {
                      const defaultRange = getDefaultDateRange();
                      setDraftDateRange(defaultRange);
                      setDateRange(defaultRange);
                    }}
                  >
                    Reset to Default
                  </Button>
                  <PrimaryButton
                    text='Apply'
                    onClick={() => setDateRange(draftDateRange)}
                    disabled={
                      !draftDateRange[0] ||
                      !draftDateRange[1] ||
                      isSameDateRange(draftDateRange, dateRange)
                    }
                  />
                </Group>
              </Stack>
            </Popover.Dropdown>
          </Popover>
        </Group>

        {/* Key Metrics Cards */}
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing='lg'>
          {/* Total Courses */}
          <Paper shadow='sm' p='lg' radius='md' withBorder>
            <Group justify='space-between' mb='xs'>
              <Text size='sm' c='dimmed' fw={500}>
                Total Courses
              </Text>
              <ThemeIcon color='blue' variant='light' size='lg' radius='md'>
                <IconBook size={20} />
              </ThemeIcon>
            </Group>
            <Text size='xl' fw={700} mb='xs'>
              {overviewData?.totalCourses || 0}
            </Text>
            <Text size='xs' c='dimmed'>
              All courses in the system
            </Text>
          </Paper>

          {/* Yearly Calendar Plans */}
          <Paper shadow='sm' p='lg' radius='md' withBorder>
            <Group justify='space-between' mb='xs'>
              <Text size='sm' c='dimmed' fw={500}>
                Calendar Plans
              </Text>
              <ThemeIcon color='violet' variant='light' size='lg' radius='md'>
                <IconCalendar size={20} />
              </ThemeIcon>
            </Group>
            <Text size='xl' fw={700} mb='xs'>
              {overviewData?.yearlyCalendarDistinctCount || 0}
            </Text>
            <Text size='xs' c='dimmed'>
              Scheduled course plans
            </Text>
          </Paper>

          <Paper
            shadow='sm'
            p='lg'
            radius='md'
            withBorder
            style={{
              cursor:
                (overviewData?.statusCounts?.find((s) => s.status === 'Need Review')?.courses
                  ?.length ?? 0) > 0
                  ? 'pointer'
                  : 'default',
              transition: 'all 0.2s ease'
            }}
            onClick={() => {
              const needReviewStatus = overviewData?.statusCounts?.find(
                (s) => s.status === 'Need Review'
              );
              if (
                needReviewStatus &&
                needReviewStatus.courses?.length &&
                needReviewStatus.courses.length > 0
              ) {
                handleViewCourses(
                  `Need Review Courses (${needReviewStatus.count})`,
                  needReviewStatus.courses
                );
              }
            }}
          >
            <Group justify='space-between' mb='xs'>
              <Text size='sm' c='dimmed' fw={500}>
                Need Review
              </Text>
              <ThemeIcon color='orange' variant='light' size='lg' radius='md'>
                <IconAlertCircle size={20} />
              </ThemeIcon>
            </Group>
            <Text size='xl' fw={700} mb='xs'>
              {overviewData?.statusCounts?.find((s) => s.status === 'Need Review')?.count || 0}
            </Text>
            <Text size='xs' c='dimmed'>
              Courses pending review
            </Text>
          </Paper>

          {/* Total Activities */}
          <Paper shadow='sm' p='lg' radius='md' withBorder>
            <Group justify='space-between' mb='xs'>
              <Text size='sm' c='dimmed' fw={500}>
                Activity Reuse Ratio
              </Text>
              <ThemeIcon color='teal' variant='light' size='lg' radius='md'>
                <IconActivity size={20} />
              </ThemeIcon>
            </Group>
            <Text size='xl' fw={700} mb='xs'>
              {(overviewData?.activityTypeCounts?.scrom || 0) +
                (overviewData?.activityTypeCounts?.resource || 0) +
                (overviewData?.activityTypeCounts?.other || 0)}
            </Text>
            <Group gap={6} mt='xs'>
              <Badge size='xs' variant='light' color='cyan'>
                SCORM: {overviewData?.activityTypeCounts?.scrom || 0}
              </Badge>
              <Badge size='xs' variant='light' color='cyan'>
                Resource: {overviewData?.activityTypeCounts?.resource || 0}
              </Badge>
              <Badge size='xs' variant='light' color='grape'>
                Other: {overviewData?.activityTypeCounts?.other || 0}
              </Badge>
            </Group>
          </Paper>
        </SimpleGrid>

        <Grid gutter='lg'>
          {/* Course Status Distribution */}
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Paper shadow='sm' p='lg' radius='md' withBorder h='100%'>
              <Group justify='space-between' mb='md'>
                <div>
                  <Text size='lg' fw={600}>
                    Course Status Distribution
                  </Text>
                  <Text size='xs' c='dimmed' mt={4}>
                    Breakdown by current status
                  </Text>
                </div>
              </Group>

              {statusData.length > 0 ? (
                <Stack gap='lg'>
                  <Center>
                    <RingProgress
                      size={200}
                      thickness={20}
                      sections={statusData.map((item) => ({
                        value: item.percentage,
                        color: item.color,
                        tooltip: `${item.status}: ${item.count} (${item.percentage.toFixed(1)}%)`
                      }))}
                      label={
                        <Center>
                          <Stack gap={0} align='center'>
                            <Text size='xl' fw={700}>
                              {overviewData?.totalCourses}
                            </Text>
                            <Text size='xs' c='dimmed'>
                              Courses
                            </Text>
                          </Stack>
                        </Center>
                      }
                    />
                  </Center>

                  <Stack gap='xs'>
                    {statusData.map((item) => (
                      <Paper
                        key={item.status}
                        p='xs'
                        withBorder
                        style={{
                          cursor: item.courses.length > 0 ? 'pointer' : 'default',
                          transition: 'all 0.2s ease'
                        }}
                        onClick={() => {
                          if (item.courses.length > 0) {
                            handleViewCourses(
                              `${item.status} Courses (${item.count})`,
                              item.courses
                            );
                          }
                        }}
                      >
                        <Group justify='space-between'>
                          <Group gap='xs'>
                            <Box
                              w={12}
                              h={12}
                              style={{
                                backgroundColor: `var(--mantine-color-${item.color}-6)`,
                                borderRadius: '50%'
                              }}
                            />
                            <Text size='sm'>{item.status || 'Undefined'}</Text>
                          </Group>
                          <Group gap='xs'>
                            <Text size='sm' fw={500}>
                              {item.count}
                            </Text>
                            <Text size='xs' c='dimmed'>
                              ({item.percentage.toFixed(1)}%)
                            </Text>
                            {item.courses.length > 0 && (
                              <IconChevronRight size={14} color='var(--mantine-color-gray-5)' />
                            )}
                          </Group>
                        </Group>
                      </Paper>
                    ))}
                  </Stack>

                  <Button
                    variant='light'
                    fullWidth
                    rightSection={<IconChevronRight size={16} />}
                    onClick={() => navigate('/app/lcms/courses')}
                  >
                    View All Courses
                  </Button>
                </Stack>
              ) : (
                <Center h={200}>
                  <Text c='dimmed'>No status data available</Text>
                </Center>
              )}
            </Paper>
          </Grid.Col>

          {/* Yearly Calendar Timeline */}
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Paper shadow='sm' p='lg' radius='md' withBorder h='100%'>
              <Group justify='space-between' mb='md'>
                <div>
                  <Text size='lg' fw={600}>
                    Course Planning
                  </Text>
                  <Text size='xs' c='dimmed' mt={4}>
                    Upcoming course plans
                  </Text>
                </div>
                <Group gap='xs'>
                  <Badge variant='light' color='violet'>
                    {overviewData?.yearlyCalendarDistinctCount || 0} Plans
                  </Badge>
                  <Badge variant='light' color='blue'>
                    {totalCoursesInPlans} Courses
                  </Badge>
                </Group>
              </Group>

              {groupedCalendarCourses.length > 0 ? (
                <Box style={{ maxHeight: 400, overflowY: 'auto' }}>
                  <Accordion variant='contained' radius='sm'>
                    {groupedCalendarCourses.map((group, idx) => (
                      <Accordion.Item key={idx} value={group.month}>
                        <Accordion.Control icon={<IconCalendarEvent size={16} />}>
                          <Group justify='space-between' pr='md'>
                            <Text size='sm' fw={600}>
                              {group.month}
                            </Text>
                            <Badge size='sm' variant='light' color='violet'>
                              {group.courses.length} {group.courses.length === 1 ? 'plan' : 'plans'}
                            </Badge>
                          </Group>
                        </Accordion.Control>
                        <Accordion.Panel>
                          <Stack gap='xs'>
                            {group.courses.map((course) => {
                              // Count unique courses in this plan
                              const uniqueCoursesCount = course.courses
                                ? new Set(course.courses.map((c) => c.courseId)).size
                                : 0;

                              return (
                                <MantineCard
                                  key={course.msLCMSPlanningID}
                                  padding='sm'
                                  withBorder
                                  radius='sm'
                                >
                                  <Group justify='space-between' wrap='nowrap'>
                                    <Box
                                      style={{
                                        flex: 1,
                                        minWidth: 0,
                                        cursor: 'pointer'
                                      }}
                                      onClick={() =>
                                        navigate(
                                          `/app/yearly-calendar/schedule-plan/detail/${course.msLCMSPlanningID}`
                                        )
                                      }
                                    >
                                      <Text size='sm' fw={500} lineClamp={1}>
                                        {course.planningName}
                                      </Text>
                                      <Text size='xs' c='dimmed' mt={4}>
                                        {dayjs(course.planningStart).format('MMM DD')} -{' '}
                                        {dayjs(course.planningEnd).format('MMM DD, YYYY')}
                                      </Text>
                                      {uniqueCoursesCount > 0 && (
                                        <Badge size='xs' variant='light' color='blue' mt={4}>
                                          {uniqueCoursesCount}{' '}
                                          {uniqueCoursesCount === 1 ? 'course' : 'courses'}
                                        </Badge>
                                      )}
                                    </Box>
                                    <Group gap='xs'>
                                      {course.courses && course.courses.length > 0 && (
                                        <Button
                                          size='xs'
                                          variant='subtle'
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleViewCourses(
                                              `${course.planningName} - Courses`,
                                              course.courses
                                            );
                                          }}
                                        >
                                          View Courses
                                        </Button>
                                      )}
                                      <IconChevronRight
                                        size={16}
                                        color='var(--mantine-color-gray-5)'
                                      />
                                    </Group>
                                  </Group>
                                </MantineCard>
                              );
                            })}
                          </Stack>
                        </Accordion.Panel>
                      </Accordion.Item>
                    ))}
                  </Accordion>
                </Box>
              ) : (
                <Center h={200}>
                  <Stack align='center' gap='xs'>
                    <IconCalendar size={48} stroke={1.5} color='var(--mantine-color-gray-4)' />
                    <Text c='dimmed' size='sm'>
                      No calendar plans in selected date range
                    </Text>
                  </Stack>
                </Center>
              )}

              {groupedCalendarCourses.length > 0 && (
                <Button
                  variant='light'
                  fullWidth
                  mt='md'
                  rightSection={<IconChevronRight size={16} />}
                  onClick={() => navigate('/app/yearly-calendar/schedule-plan')}
                >
                  View All Schedule Plans
                </Button>
              )}
            </Paper>
          </Grid.Col>

          {/* Course by Area Chart */}
          <Grid.Col span={12}>
            <Paper shadow='sm' p='lg' radius='md' withBorder>
              <Group justify='space-between' mb='md'>
                <div>
                  <Text size='lg' fw={600}>
                    Courses by Area
                  </Text>
                  <Text size='xs' c='dimmed' mt={4}>
                    Distribution of courses across different areas
                  </Text>
                </div>
                <Badge variant='light' color='blue'>
                  {totalCoursesByArea} Total
                </Badge>
              </Group>

              {courseByAreaData.length > 0 ? (
                <Stack gap='md'>
                  {courseByAreaData.map((item, index) => {
                    const percentage =
                      totalCoursesByArea > 0 ? (item.total / totalCoursesByArea) * 100 : 0;
                    const colors = [
                      'blue',
                      'violet',
                      'grape',
                      'pink',
                      'red',
                      'orange',
                      'yellow',
                      'lime',
                      'green',
                      'teal',
                      'cyan',
                      'indigo'
                    ];
                    const color = colors[index % colors.length];

                    return (
                      <Paper
                        key={item.area}
                        p='sm'
                        withBorder
                        style={{
                          cursor: item.courses?.length > 0 ? 'pointer' : 'default',
                          transition: 'all 0.2s ease'
                        }}
                        onClick={() => {
                          if (item.courses?.length > 0) {
                            handleViewCourses(
                              `${item.area} - Courses (${item.total})`,
                              item.courses
                            );
                          }
                        }}
                      >
                        <Group justify='space-between' mb={6}>
                          <Text size='sm' fw={500}>
                            {item.area}
                          </Text>
                          <Group gap={8}>
                            <Text size='sm' fw={600}>
                              {item.total}
                            </Text>
                            <Text size='xs' c='dimmed'>
                              ({percentage.toFixed(1)}%)
                            </Text>
                            {item.courses?.length > 0 && (
                              <IconChevronRight size={14} color='var(--mantine-color-gray-5)' />
                            )}
                          </Group>
                        </Group>
                        <Box
                          style={{
                            width: '100%',
                            height: 8,
                            backgroundColor: 'var(--mantine-color-gray-2)',
                            borderRadius: 4,
                            overflow: 'hidden'
                          }}
                        >
                          <Box
                            style={{
                              width: `${percentage}%`,
                              height: '100%',
                              backgroundColor: `var(--mantine-color-${color}-6)`,
                              transition: 'width 0.3s ease'
                            }}
                          />
                        </Box>
                      </Paper>
                    );
                  })}
                </Stack>
              ) : (
                <Center h={200}>
                  <Text c='dimmed'>No area data available</Text>
                </Center>
              )}
            </Paper>
          </Grid.Col>
        </Grid>
      </Stack>

      {/* Course List Modal */}
      <Modal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        title={
          <Group gap='xs'>
            <Text fw={600}>{modalTitle}</Text>
            <Badge variant='light' size='lg'>
              {modalCourses.length}
            </Badge>
          </Group>
        }
        size='lg'
        scrollAreaComponent={ScrollArea.Autosize}
        styles={{
          title: { width: '100%' }
        }}
      >
        <Stack gap='xs'>
          {modalCourses.length > 0 ? (
            modalCourses.map((course) => (
              <Box
                key={course.courseId}
                p='md'
                style={{
                  cursor: 'pointer',
                  borderRadius: '8px',
                  border: '1px solid var(--mantine-color-gray-3)',
                  transition: 'all 0.15s ease',
                  backgroundColor: 'var(--mantine-color-white)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--mantine-color-gray-0)';
                  e.currentTarget.style.borderColor = 'var(--mantine-color-blue-3)';
                  e.currentTarget.style.transform = 'translateX(4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--mantine-color-white)';
                  e.currentTarget.style.borderColor = 'var(--mantine-color-gray-3)';
                  e.currentTarget.style.transform = 'translateX(0)';
                }}
                onClick={() => {
                  navigate(`/app/lcms/courses/${course.courseId}`);
                  setModalOpened(false);
                }}
              >
                <Group justify='space-between' align='center' wrap='nowrap'>
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Text size='sm' fw={500} lineClamp={1} mb={4}>
                      {course.courseName}
                    </Text>
                    {course.category && (
                      <Badge size='sm' variant='dot' color='blue'>
                        {course.category}
                      </Badge>
                    )}
                  </Box>
                  <IconChevronRight size={18} stroke={1.5} style={{ flexShrink: 0 }} />
                </Group>
              </Box>
            ))
          ) : (
            <Center h={200}>
              <Stack align='center' gap='xs'>
                <IconBook size={48} stroke={1.5} color='var(--mantine-color-gray-4)' />
                <Text c='dimmed' size='sm'>
                  No courses available
                </Text>
              </Stack>
            </Center>
          )}
        </Stack>
      </Modal>
    </Container>
  );
};

export default LCMSOverviewPage;
